import { Container, Ellipsis, LucideProps, Mail, MailOpen, Settings } from 'lucide-react';
import React from 'react';

import { Heading } from '@/components/heading';
import { Text } from '@/components/text';
import { Tooltip } from '@/components/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DATE_MONTH_FORMAT, NOTIFICATION } from '@/lib/constants';
import { FullUserNotificationModel, NotificationGroup } from '@/lib/models/notification/types';
import { formatDateTimeForUser } from '@/utils/format/date-time';
import { tv } from '@/utils/styles';

export interface NotificationItemProps {
  fullNotification: FullUserNotificationModel;
  onMarkAsRead: (id: string, isRead: boolean) => void;
  onClick: () => void;
}

const ICON_MAP: Record<NotificationGroup, React.FunctionComponent<LucideProps>> = {
  [NOTIFICATION.GROUP.TRACKING]: Container,
  [NOTIFICATION.GROUP.INVITE]: Mail,
  [NOTIFICATION.GROUP.SYSTEM]: Settings,
} as const;

const NotificationItem = ({ fullNotification, onMarkAsRead, onClick }: NotificationItemProps) => {
  const { notification } = fullNotification;
  const Icon = ICON_MAP[notification.group];
  const shortDate = notification.createdAt ? formatDateTimeForUser(notification.createdAt, DATE_MONTH_FORMAT) : '';
  const fullDate = notification.createdAt ? formatDateTimeForUser(notification.createdAt) : '';

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === 'enter' || e.key === 'space') {
      onClick();
    }
  };

  const { wrapper, heading, text, contentWrapper, iconWrapper, dot, menuItem } = styles({
    isRead: fullNotification.isRead,
  });

  return (
    <div role="button" tabIndex={0} className={wrapper()} onClick={onClick} onKeyUp={handleKeyUp}>
      <div className={iconWrapper()}>
        <div className={dot()} />
        <Icon size={16} className="text-lightBlue-500" />
      </div>
      <div className={contentWrapper()}>
        <Heading className={heading()} as="h3" variant="h4">
          {notification.title}
        </Heading>
        <Text className={text()}>{notification.description}</Text>
      </div>
      <div>
        <Tooltip content={fullDate}>
          <Text className={text({ className: 'whitespace-nowrap' })}>{shortDate}</Text>
        </Tooltip>
      </div>
      <div>
        <DropdownMenu>
          <DropdownMenuTrigger
            className="size-5 cursor-pointer rounded-md hover:bg-lightBlue-500 hover:text-white"
            asChild
            onClick={(e) => e.stopPropagation()}
          >
            <Ellipsis className={text()} />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-white">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                if (fullNotification.id) {
                  onMarkAsRead(fullNotification.id, !fullNotification.isRead);
                }
              }}
              className={menuItem()}
              icon={fullNotification.isRead ? <Mail size={16} /> : <MailOpen size={16} className="mb-0.5" />}
            >
              Mark as {fullNotification.isRead ? 'unread' : 'read'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    wrapper: 'flex w-full cursor-pointer gap-2 rounded-md p-2 hover:bg-blue-50',
    iconWrapper: 'relative mt-1 grid size-6 min-w-6 place-items-center rounded-full bg-blue-50',
    dot: 'absolute right-0 top-0 size-1 rounded-full bg-error',
    contentWrapper: 'grow',
    heading: 'm-0 text-baseSm',
    text: 'm-0 text-baseSm text-text-secondary',
    menuItem: 'flex cursor-pointer gap-1 hover:bg-lightBlue-50',
  },
  variants: {
    isRead: {
      true: {
        dot: 'hidden',
        wrapper: 'opacity-70 grayscale hover:opacity-100 hover:grayscale-0',
      },
    },
  },
});

export { NotificationItem };
